<template>
	<div>
		<v-row class="ma-0">
			<!-- Pannello sinistro: Azioni configurate -->
			<v-col cols="6">
				<v-card outlined class="pa-2" style="max-height: 700px; overflow: auto;">
					<!-- Azioni visibili nella tabella -->
					<v-card-title class="text-subtitle-1">
						Azioni Multiple
					</v-card-title>
					<v-divider class="mb-3"></v-divider>
					
					<div class="pa-2">
						<draggable 
							v-model="tableActions" 
							:group="{ name: 'actions' }"
							class="group-fields-container"
							:class="{'has-items': tableActions.length > 0}"
                                handle=".drag-handle"
                                ghost-class="ghost-element"
                                chosen-class="chosen-element"
							:swap-threshold="0.50">
							
							<v-card 
								v-for="action in tableActions"
								:key="action.key"
								outlined
								class="field-card mb-2">
								<v-row class="ma-0 px-2 py-1 align-center">
									<v-col cols="1" class="pa-2">
										<v-icon class="drag-handle">mdi-drag-vertical-variant</v-icon>
									</v-col>
									<v-col cols="1" class="pa-2">
										<v-icon color="primary">{{ action.icon }}</v-icon>
									</v-col>
									<v-col cols="8" class="pa-2">
										{{ action.tooltip || action.text }}
									</v-col>
									<v-col cols="1" class="pa-2">
										<v-tooltip bottom>
											<template v-slot:activator="{ on, attrs }">
												<v-btn 
													icon 
													small 
													@click="removeAction(action.key)" 
													color="error"
													v-bind="attrs"
													v-on="on">
													<v-icon>mdi-delete</v-icon>
												</v-btn>
											</template>
											<span>Rimuovi Azione</span>
										</v-tooltip>
									</v-col>
									<v-col cols="1" class="pa-2">
										<v-btn icon small @click="toggleExpand(action)" color="primary">
											<v-icon>{{ isExpanded(action) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
										</v-btn>
									</v-col>
								</v-row>

								<div v-show="isExpanded(action)">
									<v-divider></v-divider>
									<div class="pa-4">
										<action-role-permissions :permissions="action.permissions" :roles="roles"></action-role-permissions>
									</div>
								</div>
							</v-card>
							
							<div v-show="tableActions.length === 0" class="empty-placeholder text-center pa-4">
								<p class="text-subtitle-2 grey--text">
									Trascina qui le azioni che vuoi mostrare nella tabella
								</p>
							</div>
						</draggable>
					</div>

					<!-- Pulsante svuota tutto -->
					<v-card-actions class="pt-3">
						<v-row no-gutters>
							<v-col cols="12">
								<v-btn 
									block
									color="error"
									@click="resetConfiguration"
									:disabled="!tableActions.length">
									<v-icon left>mdi-delete-sweep</v-icon>
									Svuota tutto
								</v-btn>
							</v-col>
						</v-row>
					</v-card-actions>
				</v-card>
			</v-col>

			<!-- Pannello destro: Azioni disponibili -->
			<v-col cols="6">
				<v-card outlined class="pa-2" style="max-height: 700px; overflow: auto;">
					<v-card-title class="text-subtitle-1">
						Azioni Disponibili
						<v-spacer></v-spacer>
						<v-text-field
							v-model="searchAction"
							append-icon="mdi-magnify"
							label="Cerca"
							hide-details
							dense
							outlined
							clearable
							style="max-width: 240px"
						></v-text-field>
					</v-card-title>
					
					<v-divider class="mb-3"></v-divider>
					
					<draggable
						class="available-actions"
						style="min-height: 48px;"
						:list="availableActions"
						:group="{ 
							name: 'actions', 
							pull: 'clone', 
							put: true,
							revertClone: true
						}"
						:sort="false"
						:clone="cloneAction">
						
						<template v-for="action in availableActions">
							<v-card 
								v-if="matchesSearch(action)"
								:key="action.key"
								class="cursor-pointer mb-1"
								outlined>
								<v-row class="ma-0 px-2 py-1 align-center">
									<v-col cols="1" class="pa-2">
										<v-icon class="drag-handle">mdi-drag-vertical-variant</v-icon>
									</v-col>
									<v-col cols="1" class="pa-2">
										<v-icon color="primary">{{ action.icon }}</v-icon>
									</v-col>
									<v-col cols="10" class="pa-2">
										{{ action.tooltip || action.text }}
									</v-col>
								</v-row>
							</v-card>
						</template>
					</draggable>
				</v-card>
			</v-col>
		</v-row>
		
		<v-divider class="mt-3"></v-divider>
		
		<v-card-actions class="pa-3">
			<!-- Solo mobile: `defaults` arriva valorizzato solo dall'endpoint mobile
			     (sez. 6 del contratto). Rimette lo stato locale, il salvataggio
			     resta a mano come per la card mobile. -->
			<v-btn v-if="defaults" text @click="restoreDefaults">
				Ripristina predefiniti
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn color="primary" @click="saveConfiguration">
				Salva Configurazione
			</v-btn>
		</v-card-actions>
	</div>
</template>

<script>
import draggable from 'vuedraggable'
import ActionRolePermissions, { defaultActionPermissions } from './ActionRolePermissions.vue'

export default {
	name: 'ActionsTableSelector',
	components: {
		draggable,
		ActionRolePermissions
	},
	
	props: {
		modelDef: {
			type: String,
			required: true
		},
		mobile: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			isLoaded: false,
			originalButtonDefs: [],    // Tiene traccia della definizione originale dei pulsanti
			tableActions: [],          // Pulsanti configurati per la tabella
			originalConfiguration: {}, // Configurazione originale per il reset
			defaults: null,            // predefiniti (solo endpoint mobile), per "Ripristina predefiniti"
			searchAction: '',
			roles: [],
			expandedActions: {}
		}
	},
	
	computed: {
		endpointUrl() {
			return this.mobile ? 'settings/actions-mobile-model/' : 'settings/actions-model/'
		},
		// Azioni disponibili sono quelle che non sono già nei pulsanti configurati
		availableActions() {
			const usedKeys = new Set(this.tableActions.map(a => a.key));
			return this.originalButtonDefs.filter(action => !usedKeys.has(action.key));
		},
		
		// Formato della configurazione da salvare (key, order e permessi per ruolo)
		configuration() {
			const actionsObj = {};
			this.tableActions.forEach((action, index) => {
				actionsObj[action.key] = {
					order: index + 1,
					key: action.key,
					permissions: action.permissions
				};
			});
			
			return actionsObj;
		}
	},
	
	methods: {
		// Carica la configurazione dal backend
		async loadConfiguration() {
			try {
				const [response, rolesResponse] = await Promise.all([
					this.axios.post(this.endpointUrl, {
						model: this.modelDef,
						type: 'table'
					}),
					this.axios.post('getoptionfields/', {model: 'Role', relation_field: 'name', key: 'text', value: 'value'})
				]);
				this.roles = rolesResponse.data.option_fields;
				
				// Memorizza la definizione originale dei pulsanti
				this.originalButtonDefs = response.data.buttonDefs || [];

				// Solo endpoint mobile: predefiniti nella stessa forma della config salvata
				this.defaults = response.data.defaults || null;

				// Se esiste una configurazione salvata, prepara i pulsanti
				if (response.data.actions_table_configuration) {
					this.processConfiguration(response.data.actions_table_configuration);
				}
				
				// Salva la configurazione originale per il reset
				this.originalConfiguration = [...this.tableActions];
				
				this.isLoaded = true;
				this.$emit('loaded');
				
			} catch (error) {
				console.error('Error loading table actions configuration:', error);
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore durante il caricamento della configurazione',
					value: true,
					color: 'error'
				});
			}
		},

		// Processa la configurazione caricata e prepara l'array di azioni
		processConfiguration(config) {
			// Preparazione azioni tabella
			this.tableActions = this.processActionConfig(
				config, 
				this.originalButtonDefs
			);
		},
		
		// Crea un array di azioni con tutti i dettagli a partire dalla config salvata (key, order, permessi)
		processActionConfig(configObj, buttonDefs) {
			return Object.entries(configObj)
				.map(([key, value]) => {
					// Trova la definizione completa del pulsante
					const buttonDef = buttonDefs.find(button => button.key === key);
					if (!buttonDef) return null;
					
					// Restituisce un nuovo oggetto con tutte le proprietà originali
					return {
						...buttonDef,
						order: value.order,
						permissions: value.permissions || defaultActionPermissions()
					};
				})
				.filter(action => action !== null) // Rimuovi eventuali null
				.sort((a, b) => a.order - b.order); // Ordina per order
		},

		// Clona un'azione quando viene trascinata
		cloneAction(action) {
			return { ...action, permissions: defaultActionPermissions() };
		},

		toggleExpand(action) {
			this.$set(this.expandedActions, action.key, !this.expandedActions[action.key]);
		},

		isExpanded(action) {
			return Boolean(this.expandedActions[action.key]);
		},

		// Cerca nell'elenco delle azioni disponibili
		matchesSearch(action) {
			if (!this.searchAction) return true;
			const searchTerm = this.searchAction.toLowerCase();
			const searchField = (action.tooltip || action.text || "").toLowerCase();
			return searchField.includes(searchTerm);
		},

		// Reimposta la configurazione allo stato originale
		resetConfiguration() {
			// Svuota l'array delle azioni
			this.tableActions = [];
		},

		// Rimette i predefiniti nello stato locale: il salvataggio resta a mano
		// (pulsante "Salva Configurazione"), come per la card mobile.
		restoreDefaults() {
			if (!this.defaults) return;
			this.tableActions = this.processActionConfig(this.defaults, this.originalButtonDefs);
			this.$store.commit('setSnackbar', {
				timeout: 5000,
				text: 'Predefiniti ripristinati: premi "Salva Configurazione" per applicarli',
				value: true,
				color: 'info'
			});
		},

		// Rimuove un'azione dall'array
		removeAction(key) {
			const index = this.tableActions.findIndex(a => a.key === key);
			if (index !== -1) {
				this.tableActions.splice(index, 1);
			}
		},

		// Salva la configurazione
		async saveConfiguration() {
			try {
				const configKey = this.mobile ? 'actions_table_mobile_configuration' : 'actions_table_configuration'
				await this.axios.post('settings/', {
					model: this.modelDef,
					[configKey]: this.configuration
				});

				// Aggiorna la configurazione originale dopo il salvataggio
				this.originalConfiguration = [...this.tableActions];

				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Configurazione delle azioni salvata con successo',
					value: true,
					color: 'success'
				});

			} catch (error) {
				console.error('Error saving table actions configuration:', error);
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore durante il salvataggio della configurazione',
					value: true,
					color: 'error'
				});
			}
		}
	},
	
	mounted() {
		this.loadConfiguration();
	}
}
</script>

<style scoped>
.group-fields-container  {
	min-height: 100px;
	margin: 12px;
	border-radius: 8px;
	border: 1px solid #ececec;        
	position: relative;
}

.group-fields-container.has-items {
	border: none;
}

.pa-2 .group-fields-container {
	margin: 0;
}

.field-card {
	transition: all 0.3s ease;
}

.empty-placeholder {
	min-height: 80px;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
}

.ghost-element {
	background: #BBDEFB !important; 
	opacity: 0.5;
}

.chosen-element {
	background: #BBDEFB !important; 
	opacity: 0.5;
}    

.drag-handle {
	cursor: move;
}

.cursor-pointer {
	cursor: pointer;
}
</style>
